import faker from 'faker'
import { type ApolloClient, useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { type PipelineStep } from '../types/attributeManagement.types'
import {
  GetStepVersionDocument,
  type GetStepVersionQuery,
  type GetStepVersionQueryVariables,
  type Instacart_Catalog_Catflow_V1_StepVersion,
} from '.'

export interface GetStepVersionsResponse {
  data: { [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion }
  isLoading: boolean
  error: unknown
  addToCache: (stepId: string, stepVersion: Instacart_Catalog_Catflow_V1_StepVersion) => void
}

export const fetchStepVersion = (
  client: ApolloClient<unknown>,
  variables: GetStepVersionQueryVariables
) =>
  client.query<GetStepVersionQuery, GetStepVersionQueryVariables>({
    query: GetStepVersionDocument,
    variables: variables,
  })

export const useGetBulkStepVersionQuery = (steps: PipelineStep[]) => {
  const client = useApolloClient()
  const [responses, setResponses] = useState<{
    [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion
  } | null>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<unknown>(null)

  // Use a ref to track responses without causing callback recreation
  const responsesRef = useRef(responses)

  // Keep ref in sync with state
  useEffect(() => {
    responsesRef.current = responses
  }, [responses])

  const fetchStepVersions = useCallback(
    async (steps: PipelineStep[]) => {
      try {
        setIsLoading(true)

        const queries = steps.map(step => {
          if (!step?.stepId || !step?.version) return null
          const idempotencyKey = faker.random.uuid()
          const idemKey = idempotencyKey + step?.stepId + step?.version

          // Check against ref to avoid re-fetching already cached data
          if (responsesRef.current?.[step.stepId]?.version == step.version) return null
          const result = fetchStepVersion(client, {
            stepId: step.stepId || '',
            version: step.version || '',
            clientId: 'catalog_camp_ipp',
            idempotencyKey: idemKey,
          })
          return result
        })

        const results = await Promise.all(queries)

        // Create a map of new stepId to payloads
        const newStepIdToVersionMap: {
          [stepId: string]: Instacart_Catalog_Catflow_V1_StepVersion
        } = {}
        for (const result of results) {
          if (!result) continue
          const stepId =
            result.data.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion?.stepVersion?.stepId
          const response =
            result.data.instacart_catalog_catflow_v1_CatflowAPI_GetStepVersion?.stepVersion
          if (stepId === undefined || response === undefined) continue
          newStepIdToVersionMap[stepId] = response
        }

        // Merge with existing responses instead of replacing
        if (Object.keys(newStepIdToVersionMap).length > 0) {
          setResponses(prevResponses => ({
            ...(prevResponses || {}),
            ...newStepIdToVersionMap,
          }))
        }
      } catch (e) {
        setError(e)
      } finally {
        setIsLoading(false)
      }
    },
    [client]
  )

  useEffect(() => {
    fetchStepVersions(steps)
  }, [fetchStepVersions, steps])

  // Function to manually add to cache
  const addToCache = useCallback(
    (stepId: string, stepVersion: Instacart_Catalog_Catflow_V1_StepVersion) => {
      setResponses(prevResponses => ({
        ...(prevResponses || {}),
        [stepId]: stepVersion,
      }))
    },
    []
  )

  const output: GetStepVersionsResponse = {
    data: responses ?? {},
    isLoading,
    error,
    addToCache,
  }
  return output
}
